import {
  Avatar,
  Button,
  Clickable,
  Col,
  Dropdown,
  Icon,
  Input,
  Row,
  ShoutoutPresets,
  Text,
  useShoutoutPresets
} from 'components';
import { theme } from 'lib';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { UploadMedia } from '../../../components';
import { submit } from '../lib';

const ReadOnlyField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const FieldLabel = styled.span`
  font-size: 16px;
  color: #888;
  white-space: nowrap;
`;

const FieldValue = styled.span`
  font-size: 16px;
  color: #333;
  font-weight: 600;
  text-align: right;
`;

// keeps the (light-styled) team dropdown legible on the gradient backdrop
const TeamPickerCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px 0;
  border-radius: 12px;
  background-color: #fff;
`;

// Styled ReactPlayer for responsiveness
const Image = styled.img`
  width: 296px;
  height: 440px;
  object-fit: cover;
`;

const StyledReactPlayer = styled(ReactPlayer)`
  min-height: 360px;
  max-width: 360px;
  height: auto; // Maintain aspect ratio
  width: 360px; // Ensure the player takes full width of its container
`;

const ShoutoutDetails = ({ data, setData, handleClose, handleNext, onShoutoutPosted, visible }) => {
  const dispatch = useDispatch(),
    firstMedia =
      (data.media?.length && data.media[0].preview) ||
      data.metaData?.image ||
      (data.youtubeId && `https://img.youtube.com/vi/${data.youtubeId}/0.jpg`),
    video = data.media?.find((x) => x.file?.type?.includes('video')),
    { t } = useTranslation();

  // team shout-outs are text-only, so the uploader isn't offered for them.
  // a game shout-out always targets one of the two teams, so it counts too.
  const isTeamTarget = !!data.eventObject || data.entity?.type === 'team';

  // preset messages are a team-only feature, same as on the CTA share-link flow.
  // for a game the target team is picked in the dropdown above, so the presets
  // follow whichever team is currently selected.
  const {
    presets,
    fallback,
    loading: presetsLoading,
    refetch: refetchPresets,
    getPresetText
  } = useShoutoutPresets({
    teamId: isTeamTarget ? data.entity?._id : null,
    teamName: data.entity?.name,
    // the modal is hidden, not unmounted — reopening it has to pull a fresh
    // list, since a preset just used is no longer eligible
    active: visible
  });

  // kept on `data` rather than local state: the modal is only hidden (not
  // unmounted) on close, so the selection has to reset with the rest of the draft
  const selectedPresetId = data.presetId || null;

  const selectedPreset = presets.find((x) => x._id === selectedPresetId);
  // a preset and a custom message are mutually exclusive — whichever is active
  // is what gets posted
  const message = selectedPreset ? getPresetText(selectedPreset) : data.comment?.trim();

  function handleChange(opt) {
    setData((data) => ({
      ...data,
      ...opt
    }));
  }

  function handlePresetSelect(preset) {
    // the preset takes over as the message, so drop any custom draft
    handleChange({ presetId: preset?._id || null, ...(preset && { comment: '' }) });
  }

  // focusing the composer is the other half of the toggle: it drops the preset
  function handleComposerFocus() {
    handleChange({ presetId: null });
  }

  // the preset went on cooldown between fetch and post (e.g. a second tab) —
  // clear the stale selection and resync the pills with the server
  function handlePresetUnavailable() {
    handleChange({ presetId: null });
    refetchPresets();
  }

  async function handleSubmit() {
    if (data.eventObject || data.entity?.type === 'team') {
      // the resolved preset text is what actually gets posted as the comment
      return submit({
        dispatch,
        data: { ...data, comment: message },
        handleClose,
        onPresetUnavailable: handlePresetUnavailable,
        onSuccess: onShoutoutPosted
      });
    }
    return handleNext();
  }

  return (
    <React.Fragment>
      <Row wid="100%" marg="24px 0 0">
        <Col marg="0 0 0 10px" wid="100%">
          {/* the team picker keeps its light styling — a white card so it stays
              readable on the gradient */}
          {data.eventObject && (
            <TeamPickerCard>
              <Dropdown
                title={t('common.team')}
                options={[data.eventObject.away, data.eventObject.home]}
                // presets are per team, so switching teams drops the picked preset
                handleSelect={(opt) => handleChange({ entity: opt, presetId: null })}
                selected={data.entity?.name}
                contain
              />
            </TeamPickerCard>
          )}

          {/* a player target keeps the plain read-only line; a team gets the
              same emblem + name + @username hero as the CTA compose screen */}
          {!isTeamTarget && !data.eventObject && (
            <ReadOnlyField>
              <FieldLabel>{t('common.player')}:</FieldLabel>
              <FieldValue>{data.entity?.name}</FieldValue>
            </ReadOnlyField>
          )}

          {isTeamTarget && (
            <React.Fragment>
              {!!data.entity?._id && (
                <Row centerAll noFlex wid="100%" marg={data.eventObject ? '24px 0 0' : '0'}>
                  <Avatar user={data.entity} showVerified />

                  {/* name beside the emblem, @username stacked under the name */}
                  <Col marg="0 0 0 12px" center noFlex>
                    <Text h2 col="#fff">
                      {data.entity?.name}
                    </Text>

                    <Text c1 med col="#fff">
                      @{data.entity?.username}
                    </Text>
                  </Col>
                </Row>
              )}

              <Row start wid="100%" noFlex marg="24px 0 0">
                <Text h4 col="#fff">
                  {t('callToAction.yourShoutoutTo', { name: data.entity?.name })}
                </Text>
              </Row>

              {/* preset messages — tap to select, tap again to write your own */}
              <Col wid="100%" marg="8px 0 0" noFlex>
                <ShoutoutPresets
                  dark
                  presets={presets}
                  fallback={fallback}
                  loading={presetsLoading}
                  getPresetText={getPresetText}
                  selectedPresetId={selectedPresetId}
                  onSelect={handlePresetSelect}
                  teamName={data.entity?.name}
                />
              </Col>

              <Row start wid="100%" noFlex marg="24px 0 0">
                <Text h4 col="#fff">
                  {t('callToAction.writeYourOwnMessage')}
                </Text>
              </Row>
            </React.Fragment>
          )}

          {/* onFocus bubbles in React, so this catches focus from the textarea */}
          <Col wid="100%" noFlex onFocus={isTeamTarget ? handleComposerFocus : undefined}>
            <Input
              marg={isTeamTarget ? '8px 0 0' : '24px 0 0'}
              wid="100%"
              bg={isTeamTarget ? '#fff' : undefined}
              multiline
              emoji={isTeamTarget}
              ht="160px"
              placeholder={t(
                isTeamTarget ? 'callToAction.typeMessageOrEmojis' : 'common.typeYourMessage'
              )}
              value={data.comment}
              onChange={(e) =>
                handleChange({ comment: e.target.value, ...(isTeamTarget && { presetId: null }) })
              }
            />
          </Col>

          {/* team shout-outs carry no media, so the post button follows the
              composer directly instead of leaving an empty media slot */}
          {!isTeamTarget && (
            <Row wid="100%" marg="24px 0 0">
              {data.media?.length ? (
                <Col pos="relative" noFlex wid="360px">
                  {/* Close Button */}
                  <Col pos="absolute" top="10px" right="10px" zIndex={2}>
                    <Clickable onClick={() => handleChange({ media: undefined })}>
                      <Col
                        ht="32px"
                        wid="32px"
                        noFlex
                        hasRadius="32px"
                        bg="rgba(210, 216, 223, 0.7)"
                        centerAll
                      >
                        <Icon type="close" dimensions={16} />
                      </Col>
                    </Clickable>
                  </Col>

                  {/* ReactPlayer for video */}
                  {/* <StyledReactPlayer
                  url={data.media[0].uri}
                  controls
                /> */}
                  {!!firstMedia && <Image src={firstMedia} />}

                  {!!video && <StyledReactPlayer url={video.uri} controls />}
                </Col>
              ) : (
                <UploadMedia ht="200px" wid="100%" setData={setData} />
              )}
            </Row>
          )}

          <Button
            marg={isTeamTarget ? '16px 0 0' : '25px 0 0'}
            big={isTeamTarget}
            wid={isTeamTarget ? '100%' : 'fit-content'}
            pad={isTeamTarget ? undefined : '0 24px'}
            // same red CTA button as the share-link compose screen, greyed out
            // until there is a message to post
            bg={isTeamTarget ? (message ? theme.SECONDARY : theme.GREY_30) : undefined}
            text={t(isTeamTarget ? 'callToAction.postShoutout' : 'common.next')}
            onClick={handleSubmit}
            disabled={!message || !data.entity?._id}
            // submit() flags this button id while the post is in flight
            action={isTeamTarget ? 'navigator' : undefined}
          />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default ShoutoutDetails;
